import { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router';

import { useActions } from '../../../../../hooks/useActions';
import { useProcessing } from '../../../../../hooks/useProcessing';
import { IPerson } from '../../../../../models/Person';
import { RoutePath } from '../../../../../models/enums/RoutePath';
import { updateModalState } from '../../../../../store/redux-slices/modals';
import { MEMBERS_LOADING_PROCESSING } from '../../../../../store/redux-slices/processes/constants';
import { ILoadSubscriptionsPayload } from '../../../../../store/saga-slices/subscriptions/airAmbulance/models';
import { EDIT_MEMBER_MODAL_NAME } from '../../../../../styledComponents/Modals/EditMemberModal/editMemberModal.const';
import { textContainsWords } from '../../../../../utils/textHelpers';
import { clearSubscriptionsList } from '../../../../../store/redux-slices/subscriptions/common';
import { loadSubscriptions } from '../../../../../store/saga-slices/subscriptions/common';
import { useNavigateSearch } from '../../../../../hooks/useNavigateSearch';
import { MemberOverviewPageParamsType } from '../../../../../models/types/RoutePath';
import { RequiredQueryParams } from './docMembers.const';
import { loadDOCMembers } from '../../../../../store/saga-slices/membership/dutyOfCare';
import {
  clearDOCMembers,
  updateDOCMembersList,
} from '../../../../../store/redux-slices/membership/dutyOfCare';
import { pageListDOCMembersSelector } from '../../../../../store/redux-slices/membership/dutyOfCare/selectors';
import { ILoadDOCMembersPayload } from '../../../../../store/saga-slices/membership/dutyOfCare/models';
import { IDOCMemberListItem } from '../../../../../models/interfaces/Membership/DutyOfCare/IDOCMemberListItem';
import { docPackagesSelector } from '../../../../../store/redux-slices/packages/dutyOfCare/selectors';
import { loadDOCPackages } from '../../../../../store/saga-slices/packages/dutyOfCare';
import { clearDOCPackages } from '../../../../../store/redux-slices/packages/dutyOfCare';

export const useDOCMembers = () => {
  const LoadMembers = useActions(loadDOCMembers);
  const ClearMembers = useActions(clearDOCMembers);
  const location = useLocation();
  const navigate = useNavigateSearch();
  const pageListMembers = useSelector(pageListDOCMembersSelector);
  const docPackages = useSelector(docPackagesSelector);

  const membersLoading = useProcessing(MEMBERS_LOADING_PROCESSING);
  const LoadDOCPackages = useActions(loadDOCPackages);
  const LoadSubscriptions = useActions(loadSubscriptions);
  const ClearSubscriptionsList = useActions(clearSubscriptionsList);
  const ClearDOCPackages = useActions(clearDOCPackages);

  const UpdateModalState = useActions(updateModalState);
  const UpdateMembersList = useActions(updateDOCMembersList);

  const { id } = useParams();

  useEffect(() => {
    const payload: ILoadSubscriptionsPayload = {
      organizationId: id,
    };
    LoadSubscriptions(payload);
  }, [LoadSubscriptions, id]);

  useEffect(() => {
    LoadDOCPackages();
  }, [LoadDOCPackages]);

  useEffect(() => {
    const params = location.search.slice(1);

    if (!textContainsWords(params, RequiredQueryParams)) {
      return;
    }

    const payload: ILoadDOCMembersPayload = {
      params,
      organizationId: id,
    };

    LoadMembers(payload);
  }, [LoadMembers, id, location.search]);

  useEffect(
    () => () => {
      ClearMembers();
      ClearDOCPackages();
      ClearSubscriptionsList();
    },
    [ClearMembers, ClearSubscriptionsList, ClearDOCPackages],
  );

  const onRowClickHandler = useCallback(
    (row: IDOCMemberListItem) => {
      const params: MemberOverviewPageParamsType = {
        memberId: row.personId,
      };
      navigate(RoutePath.MemberOverviewPage, { params });
    },
    [navigate],
  );

  const rowEditHandler = useCallback(
    (personId: string) => {
      UpdateModalState([EDIT_MEMBER_MODAL_NAME, personId]);
    },
    [UpdateModalState],
  );

  const onMemberEditedHandler = useCallback(
    (person: IPerson) => {
      UpdateMembersList(person);
    },
    [UpdateMembersList],
  );

  return {
    pageListMembers,
    membersLoading,
    onRowClickHandler,
    rowEditHandler,
    onMemberEditedHandler,
    docPackages,
  };
};
